import React, { useState, useEffect, useMemo } from 'react';
import { Assignment, Submission } from '@core/assignments/types';
import {
  SpaceUser,
  SpaceGroup,
  AssignmentData,
  DestinationAssignmentData,
  AssignmentCreationError,
} from './Conversion.types';
import Conversion from './Conversion';
import ExistingSubmissionErrorDialog from '../components/ExistingSubmissionErrorDialog';
import {
  PARENT_ASSIGNMENT_SUBMISSIONS_EXIST_ERROR,
  STUDENT_WITH_SUBMISSION_IN_EXTENSION_ERROR,
} from '../../Assignments/constants';
import getExistingSubmissions from '../../Submissions/getExistingSubmissions';
import { Box, Button, Modal, useTheme } from '@ublend-npm/aulaui-core';
import { LINK_EXTENSION_TITLE_ID } from './constants';
import { createStyles } from './ConversionModal.styles';

type ConversionModalProps = {
  dialogTitle: string;
  open: boolean;
  onModalClose?: (callback?: () => void) => void;
  convertToExtension: (
    spaceId: string,
    extension: any,
    accessToken?: string,
    user?: any
  ) => Promise<any>;
  redirectToSubmissions: (spaceId: string, assignmentId: string) => void;
  resetErrorState: () => void;
  user: {
    id?: string | number;
    firstName?: string;
  };
  accessToken?: string;
  spaceId: string;
  assignment: Assignment;
  unassignedStudents?: SpaceUser[];
  spaceGroups?: SpaceGroup[];
  getSubmissions: (id: string) => any[];
  fetchAllSubmissionsIncludingExtensions: (params: {
    accessToken?: string;
    assignmentsIds: string[];
  }) => void;
  assignments?: Assignment[];
  assignmentCreationError?: AssignmentCreationError;
  studentIdsWithExistingSubmissions: string[];
};

const hasSubmissionInParent = (assignmentCreationError) => {
  return (
    assignmentCreationError &&
    assignmentCreationError.errorCode &&
    (assignmentCreationError.errorCode ===
      PARENT_ASSIGNMENT_SUBMISSIONS_EXIST_ERROR ||
      assignmentCreationError.errorCode ===
        STUDENT_WITH_SUBMISSION_IN_EXTENSION_ERROR)
  );
};

const ConversionModal = (props: ConversionModalProps) => {
  const {
    unassignedStudents = [],
    spaceGroups = [],
    onModalClose = () => {},
    assignments = [],
    dialogTitle,
    assignment,
    assignmentCreationError,
    studentIdsWithExistingSubmissions,
    redirectToSubmissions,
    resetErrorState,
    open,
    getSubmissions,
    fetchAllSubmissionsIncludingExtensions,
  } = props;

  const { provider, id } = assignment;
  const styles = createStyles(useTheme());
  const [destinationAssignmentData, setDestinationAssignmentData] =
    useState<DestinationAssignmentData>({
      destinationAssignmentId: '',
      parentAssignmentTitle: '',
    });

  const usersWithSubmissionsInAssignment = getExistingSubmissions(
    getSubmissions(id)
  )?.map((item: Submission) => item?.user?.id);

  const getUsersWithSubmissionsInDestination = () => {
    const destinationAssignment = assignments.find(
      (item: Assignment) =>
        item.id === destinationAssignmentData?.destinationAssignmentId
    );
    const extensionsIdsFromDestination =
      destinationAssignment && destinationAssignment.extensions
        ? destinationAssignment?.extensions?.map((extension) => extension?.id)
        : [];
    const ids = [
      ...extensionsIdsFromDestination,
      destinationAssignmentData?.destinationAssignmentId,
    ];

    const userSubmissionsInAssignments = ids?.map((assignmentId) =>
      getExistingSubmissions(getSubmissions(assignmentId)).map(
        (item) => item.user.id
      )
    );

    return userSubmissionsInAssignments.reduce(
      (acc, users) => [...acc, ...users],
      []
    );
  };

  const usersWithSubmissonsInDestination =
    getUsersWithSubmissionsInDestination();

  const studentsWithValidSubmissions = usersWithSubmissionsInAssignment?.filter(
    (userId: string) =>
      !usersWithSubmissonsInDestination.find(
        (userWithSubmission: string) => userWithSubmission === userId
      )
  );

  const studentsWithSubmissionInAssignment =
    studentsWithValidSubmissions.reduce((acc, userId) => {
      const user = unassignedStudents.find(
        ({ id: studentId }) => studentId === userId
      );

      if (!user) {
        return acc;
      }

      return [
        ...acc,
        {
          ...user,
          userActionDisabled: true,
        },
      ];
    }, []);

  const [loading, setLoading] = useState(false);
  const [assignmentData, setAssignmentData] = useState<AssignmentData>({
    assignedStudents: studentsWithSubmissionInAssignment || [],
    groups: [],
    unassignedStudents: unassignedStudents || [],
    title: '',
  });

  useEffect(() => {
    resetErrorState();
  }, []);

  const studentsWithExistingSubmissions = useMemo(() => {
    if (!unassignedStudents?.length) {
      return [];
    }
    return studentIdsWithExistingSubmissions
      .map((studentId) =>
        unassignedStudents.find((student) => student.id === studentId)
      )
      .filter(Boolean);
  }, [studentIdsWithExistingSubmissions, unassignedStudents]);

  const getGreyedOutUsers = (usersInDestination, usersInAssignment) => {
    return usersInDestination.filter(
      (userId: string) => !usersInAssignment.includes(userId)
    );
  };

  const save = async () => {
    const { user, spaceId, accessToken, convertToExtension } = props;

    const studentIds = assignmentData?.unassignedStudents?.map(
      (student) => student.id
    );
    setLoading(true);

    const assignmentDetails = {
      ...assignmentData,
      parentAssignmentId: destinationAssignmentData?.destinationAssignmentId,
      classId: spaceId,
    };

    const extension = {
      ...props.assignment,
      ...assignmentDetails,
      students: studentIds,
      submissions: [],
    };

    const convertedExtension = await convertToExtension(
      spaceId,
      extension,
      accessToken,
      user
    );

    // Redirect to parent assignment if conversion is successful
    if (convertedExtension) {
      onModalClose(() =>
        redirectToSubmissions(
          spaceId,
          destinationAssignmentData?.destinationAssignmentId
        )
      );
    }
  };

  const actions = {
    primary: {
      label: 'Convert',
      isDisabled: (
        destinationAssignmentDetails: DestinationAssignmentData,
        assignmentDetails: AssignmentData
      ) => {
        return (
          !assignmentDetails?.title ||
          !assignmentDetails?.unassignedStudents?.length ||
          !destinationAssignmentDetails?.parentAssignmentTitle
        );
      },
    },
    secondary: {
      label: 'Cancel',
    },
  };

  const getActions = () => {
    if (!actions) {
      return null;
    }

    const { primary, secondary } = actions;

    const actionsConfig = {
      primary: primary && {
        label: primary.label,
        onClick: () => save(),
        disabled:
          primary.isDisabled &&
          primary.isDisabled(destinationAssignmentData, assignmentData),
        loading,
      },
      secondary: secondary && {
        label: secondary.label,
        onClick: () => {
          onModalClose();
        },
      },
    };

    return (
      <ModalActions
        primary={actionsConfig.primary}
        secondary={actionsConfig.secondary}
        styles={styles}
      />
    );
  };

  const updateDestinationAssignmentData = (newProps) => {
    // fetch submissions for destination assignment and for its extensions
    if (newProps.destinationAssignmentId) {
      const destinationAssignment: any = assignments.find(
        (item) => item.id === newProps.destinationAssignmentId
      );
      const extensionsIdsFromDestination = destinationAssignment.extensions
        ? destinationAssignment.extensions.map((extension) => extension.id)
        : [];
      const ids = [
        ...extensionsIdsFromDestination,
        newProps.destinationAssignmentId,
      ];

      fetchAllSubmissionsIncludingExtensions({
        accessToken: props.accessToken,
        assignmentsIds: ids,
      });
    }

    setDestinationAssignmentData((oldProps) => ({
      ...oldProps,
      ...newProps,
    }));
  };

  const updateAssignmentData = (newProps) => {
    setAssignmentData((oldProps) => ({
      ...oldProps,
      ...newProps,
    }));
  };

  const hasError =
    assignmentCreationError &&
    !hasSubmissionInParent(assignmentCreationError) &&
    !!assignmentCreationError.errorCode;

  if (hasError && loading) {
    setLoading(false);
  }

  return hasSubmissionInParent(assignmentCreationError) ? (
    <ExistingSubmissionErrorDialog
      open={open}
      students={studentsWithExistingSubmissions}
      provider={provider}
      onClose={() => {
        resetErrorState();
        onModalClose();
      }}
      isConversion
    />
  ) : (
    <Modal open={open} onClose={() => onModalClose()}>
      <Box
        sx={styles.linkExtensionBody}
        role="dialog"
        aria-labelledby={LINK_EXTENSION_TITLE_ID}
        tabIndex={-1}
      >
        <Conversion
          data={destinationAssignmentData}
          assignmentData={assignmentData}
          setData={updateDestinationAssignmentData}
          setAssignmentData={updateAssignmentData}
          spaceGroups={spaceGroups}
          onClose={onModalClose}
          assignments={assignments as any}
          assignment={assignment as any}
          dialogTitle={dialogTitle}
          hasError={hasError}
          greyedOutUsers={getGreyedOutUsers(
            usersWithSubmissonsInDestination,
            usersWithSubmissionsInAssignment
          )}
        />
        {getActions()}
      </Box>
    </Modal>
  );
};

const ModalActions = ({ primary, secondary, styles }) => {
  return (
    <Box sx={styles.modalActions}>
      <Button sx={styles.cancelButton} kind="secondary" {...secondary}>
        {secondary.label}
      </Button>
      <Button sx={styles.convertButton} kind="primary" {...primary}>
        {primary.label}
      </Button>
    </Box>
  );
};

ConversionModal.defaultProps = {
  onModalClose: () => {},
  unassignedStudents: [],
  spaceGroups: [],
  accessToken: '',
  assignments: [],
  assignmentCreationError: {},
};

export default React.memo(ConversionModal);
